import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LayoutDashboard, Library, MessageCircleQuestion, Sparkles, UserRound, LogOut, Menu, GraduationCap, CreditCard, ScrollText } from "lucide-react";
import { BillingBanner, daysLeft } from "@/components/Billing";

const ROLE_LABEL = { student: "Student", parent: "Parent", teacher: "Teacher", admin: "School Admin", school: "School" };

export const Brand = ({ to = "/" }) => (
  <NavLink to={to} className="flex items-center gap-2" data-testid="brand-link">
    <div className="h-9 w-9 rounded-xl bg-[#0F7B7D] text-white flex items-center justify-center">
      <GraduationCap className="h-5 w-5" />
    </div>
    <span className="font-display font-semibold text-lg tracking-tight text-[#071A22]">Scholar Campus</span>
  </NavLink>
);

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/catalog", label: "Course Library", icon: Library, id: "catalog" },
  { to: "/tutor", label: "AI Tutor", icon: MessageCircleQuestion, id: "tutor" },
  { to: "/create", label: "Create Lessons", icon: Sparkles, id: "create" },
  { to: "/ged", label: "GED Prep", icon: ScrollText, id: "ged" },
  { to: "/records/me", label: "My Records", icon: GraduationCap, id: "records" },
  { to: "/billing", label: "Billing", icon: CreditCard, id: "billing" },
  { to: "/profile", label: "Profile", icon: UserRound, id: "profile" },
];

const SidebarBody = ({ onNav }) => {
  const { user, signOut, billing } = useAuth();
  const navigate = useNavigate();
  const sub = billing?.subscription;
  return (
    <div className="flex flex-col h-full" data-testid="app-sidebar">
      <div className="px-5 pt-6 pb-4">
        <Brand to="/dashboard" />
        <div className="mt-4 inline-flex items-center rounded-full bg-[#D7F3F1] text-[#0B5B5B] text-xs font-medium px-3 py-1" data-testid="sidebar-role-chip">
          {ROLE_LABEL[user?.role] || "Member"}
          {user?.role === "student" && user?.grade_band ? ` · ${user.grade_band}` : ""}
        </div>
        {sub?.status === "trialing" && (
          <div className="mt-2 text-xs text-muted-foreground" data-testid="sidebar-trial-chip">Free trial · {daysLeft(sub.trial_end)} days left</div>
        )}
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {NAV.filter((n) => !(n.id === "billing" && user?.role === "admin") && !(n.id === "records" && user?.role !== "student") && !(n.id === "ged" && user?.role === "parent")).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onNav}
            data-testid={`app-sidebar-nav-${n.id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 h-11 px-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive ? "bg-[#D7F3F1] text-[#071A22] shadow-[var(--shadow-sm)]" : "text-[#123645]/80 hover:bg-secondary"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            {user?.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
            <AvatarFallback className="bg-[#E9E1CF] text-[#071A22] font-semibold">{(user?.name || "?").slice(0, 1).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Sign out"
            data-testid="logout-button"
            onClick={async () => {
              await signOut();
              navigate("/");
            }}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AppShell = ({ children, noBanner = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen paper-noise">
      <aside className="hidden lg:block print:hidden fixed inset-y-0 left-0 w-64 bg-[#FBFAF4] border-r border-border z-30">
        <SidebarBody />
      </aside>
      <header className="lg:hidden print:hidden sticky top-0 z-30 bg-[#FBFAF4] border-b border-border h-14 flex items-center justify-between px-4">
        <Brand to="/dashboard" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-[#FBFAF4]">
            <SidebarBody onNav={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="lg:pl-64 print:pl-0">
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-[1400px] print:p-0">{!noBanner && <div className="print:hidden"><BillingBanner /></div>}{children}</div>
      </main>
    </div>
  );
};
